import type { MeetingDocumentPreReadingSettings } from '@framework/constants/enums/meetingDocumentPreReadingPreferences';

import type { PERMISSION_SETS, PERMISSIONS } from './permissions';

type PictureProperties = {
    top: number;
    left: number;
    bottom: number;
    right: number;
    scale: number;
};

export enum Roles {
    Member = 1,
    Assistant = 2,
    Admin = 3,
    Auditor = 4,
    OfficeManager = 5,
    UserManagementAdmin = 6
}

type ProfilePicture = string;

export type User = {
    id: string;
    firstName: string;
    lastName: string;
    gender: string;
    email: string;
    phone: string;
    profilePicture: ProfilePicture;
    profilePictureFilename?: string;
    profilePictureType?: string;
    profilePictureUpdatedAt: string;
    pictureProperties: PictureProperties;
    language: string;
    hasCroppedPicture: boolean;
    handSignature: string | null;
    canReceiveNotifications: boolean;
    isOrganisationUser: boolean;
    isSherpanyUser: boolean;
    isPioneer: boolean;
    isMeetingLead?: boolean;
    isAmbassador: boolean;
    onboarding?: Record<string, boolean>;
    onboardingMeetingStatus?: string;
    allowImpersonation?: boolean;
    timezone: string;
    joinedAt: string;
    futuraeEnabled?: boolean;
    futuraeEnrolled?: boolean;
    encourageFullEnrollment?: boolean;
    preReadingPreference?: MeetingDocumentPreReadingSettings;
    ms365HandshakeStatus?: MicrosoftHandshakeStatus | null;
    mustEnrollForSecureAuthentication?: boolean;
    lastLogins: LastLogin[];
    sessionInactivityTimeoutMinutes?: number | null;
};

export type OrganisationUser = User & {
    ms365HandshakeStatus: MicrosoftHandshakeStatus | null;
};

export type MicrosoftHandshakeStatus = {
    emailAddress: string;
    hasSufficientLicenses: boolean;
    hasSufficientPermissions: boolean;
    licenseProvisioningStatus: LicenseProvisioningStatus | string | null;
};

type LastLogin = {
    created: string;
    ipAddress: string;
    platform: string;
};

export enum LicenseProvisioningStatus {
    Disabled = 'Disabled',
    ErrorStatus = 'ErrorStatus',
    PendingActivation = 'PendingActivation',
    PendingInput = 'PendingInput',
    PendingProvision = 'PendingProvision',
    Success = 'Success'
}

export type AssistsMember = {
    id: string;
} & CompactMember;

export type Assistant = {
    id: string;
    invitationStatus: string;
    isVisible: boolean;
    readonlyUserFields: string[];
    role: Roles;
    state: MEMBER_STATES;
    title: string;
    user: User;
};

export type Member = {
    classificationLevel?: number;
    created: string;
    createdBy: string;
    groups: string[];
    id: string;
    invitationStatus: string;
    isGroupManager: boolean;
    isLoggedMember: boolean;
    isMeetingOrganiser: boolean;
    isOfficeManager: boolean;
    isVisible: boolean;
    lastInvited: string | null;
    modified: string;
    readonlyUserFields: string[];
    role: Roles;
    roomId: string;
    state: MEMBER_STATES;
    title: string;
    user: User;
    assistsId?: string;
    assistsMembers: AssistsMember[];
    assistedByMembers: Assistant[];
    permissionSets: PERMISSION_SETS[];
    permissions: Record<PERMISSIONS, boolean>;
    hijackStatus?: {
        isHijacking: boolean;
        hijackUrl: string;
    } | null;
};

export type ListMember = Member & {
    groups: Group[];
};

export type PickerListMember = Pick<Member, 'id'> & {
    user: Pick<User, 'firstName' | 'lastName' | 'profilePicture'>;
    isAlreadyPicked: boolean;
};

export type CompactMember = {
    id: string;
    user: Pick<User, 'id' | 'firstName' | 'lastName' | 'profilePicture'>;
    isGroupManager: boolean;
    isMeetingOrganiser: boolean;
    role: Roles;
    state: MEMBER_STATES;
    invitationStatus?: string;
};

export type Group = {
    id: string;
    color: string;
    created: string;
    deleted: string | null;
    members: string[];
    managers: string[];
    initials: string;
    modified: string;
    name: string;
    roomId: string;
    membersWithAssistants: { [key: string]: string[] };
    isUsedInMeetings: boolean;
    isUsedInProposals: boolean;
};

type GroupShowManager = {
    id: string;
    name: string;
};

export type GroupShow = {
    color: string;
    initials: string;
    id: string;
    managers: GroupShowManager[];
    name: string;
    totalMembers: number;
    type: string;
};

export type LoggedMemberAttributes = {
    id: string;
    created: string;
    modified: string;
    title: string;
    role: number;
    isVisible: boolean;
    user: User;
    roomId: string;
    isMeetingOrganiser: boolean;
    isGroupManager: boolean;
    groups: string[];
    assistants: Member[];
    invitedAsistants: Member[];
    state: string;
    readonlyUserFields: [];
    classificationLevel: number;
    invitationStatus: string;
    assistsId?: string;
    assistsMembers: AssistsMember[];
};

export enum MEMBER_STATES {
    ACTIVE = 'active',
    DRAFT = 'draft',
    INACTIVE = 'inactive',
    INVITED = 'invited'
}

export type MemberValidation = {
    email: string;
    memberId?: string;
};

export type AssistedMember = {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture: string;
    isCurrentlyAssistingThisMember: boolean;
};
